import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Building2, Globe, MapPin, Phone, Palette, Save, Upload, Loader2,
  Facebook, Instagram, Linkedin, Twitter, FileText, Image as ImageIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_PRESETS = [
  "#0099CC", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444",
  "#EC4899", "#06B6D4", "#14B8A6", "#F97316", "#6366F1",
];

export default function OrgProfilePage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    description: "",
    website: "",
    city: "",
    country: "",
    phone: "",
    primary_color: "#0099CC",
    secondary_color: "#10B981",
    social_facebook: "",
    social_instagram: "",
    social_linkedin: "",
    social_twitter: "",
  });

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/event/organization/profile`, { headers });
      setOrg(res.data);
      setForm({
        description: res.data.description || "",
        website: res.data.website || "",
        city: res.data.city || "",
        country: res.data.country || "",
        phone: res.data.phone || "",
        primary_color: res.data.primary_color || "#0099CC",
        secondary_color: res.data.secondary_color || "#10B981",
        social_facebook: res.data.social_facebook || "",
        social_instagram: res.data.social_instagram || "",
        social_linkedin: res.data.social_linkedin || "",
        social_twitter: res.data.social_twitter || "",
      });
    } catch {
      toast.error("Erreur chargement profil");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/api/event/organization/profile`, form, { headers });
      setOrg(res.data);
      toast.success("Profil mis a jour avec succes");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/api/event/organization/logo`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setOrg(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success("Logo mis a jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload logo");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-[#0099CC]" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="org-profile-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Building2 className="w-6 h-6 text-[#F59E0B]" />
          Profil de l'organisation
        </h1>
        <p className="text-sm text-[#64748b] mt-1">Personnalisez votre organisation pour vos evenements publics</p>
      </div>

      {/* Logo Section */}
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6" data-testid="logo-section">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-[#0099CC]" /> Logo de l'organisation
        </h2>
        <div className="flex items-center gap-6">
          <div
            className="w-24 h-24 rounded-xl border-2 border-dashed border-[#2A3441] flex items-center justify-center overflow-hidden"
            style={{ backgroundColor: (form.primary_color || "#0099CC") + "15" }}
          >
            {org?.logo_url ? (
              <img src={org.logo_url} alt="Logo" className="w-full h-full object-cover rounded-xl" data-testid="org-logo-preview" />
            ) : (
              <Building2 className="w-10 h-10" style={{ color: form.primary_color || "#0099CC" }} />
            )}
          </div>
          <div>
            <input type="file" ref={fileRef} className="hidden" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="border-[#2A3441] text-[#94A3B8] hover:text-white hover:border-[#0099CC]"
              data-testid="upload-logo-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? "Upload..." : "Changer le logo"}
            </Button>
            <p className="text-xs text-[#64748b] mt-2">PNG, JPG ou WebP. Max 5 Mo.</p>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#0099CC]" /> Description
        </h2>
        <textarea
          value={form.description}
          onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Decrivez votre organisation en quelques lignes..."
          rows={4}
          className="w-full bg-[#060B14] border border-[#1E293B] rounded-lg p-3 text-white text-sm placeholder-[#64748b] resize-none focus:outline-none focus:border-[#0099CC]"
          data-testid="org-description"
        />
      </div>

      {/* Contact & Location */}
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#0099CC]" /> Contact et localisation
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#64748b] mb-1.5 block">Telephone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-2.5 w-4 h-4 text-[#64748b]" />
              <Input value={form.phone} onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="+33 6 12 34 56 78" className="pl-10 bg-[#060B14] border-[#1E293B] text-white h-10" data-testid="org-phone" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#64748b] mb-1.5 block">Site web</label>
            <div className="relative">
              <Globe className="absolute left-3 top-2.5 w-4 h-4 text-[#64748b]" />
              <Input value={form.website} onChange={e => setForm(prev => ({ ...prev, website: e.target.value }))} placeholder="https://monsite.com" className="pl-10 bg-[#060B14] border-[#1E293B] text-white h-10" data-testid="org-website" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#64748b] mb-1.5 block">Ville</label>
            <Input value={form.city} onChange={e => setForm(prev => ({ ...prev, city: e.target.value }))} placeholder="Paris" className="bg-[#060B14] border-[#1E293B] text-white h-10" data-testid="org-city" />
          </div>
          <div>
            <label className="text-xs text-[#64748b] mb-1.5 block">Pays</label>
            <Input value={form.country} onChange={e => setForm(prev => ({ ...prev, country: e.target.value }))} placeholder="France" className="bg-[#060B14] border-[#1E293B] text-white h-10" data-testid="org-country" />
          </div>
        </div>
      </div>

      {/* Brand Colors */}
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <Palette className="w-4 h-4 text-[#0099CC]" /> Couleurs de marque
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="text-xs text-[#64748b] mb-2 block">Couleur principale</label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLOR_PRESETS.map(c => (
                <button
                  key={c}
                  onClick={() => setForm(prev => ({ ...prev, primary_color: c }))}
                  className={`w-8 h-8 rounded-lg transition-all ${form.primary_color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#0B0F19] scale-110" : "hover:scale-105"}`}
                  style={{ backgroundColor: c }}
                  data-testid={`color-primary-${c}`}
                />
              ))}
              <Input
                type="color"
                value={form.primary_color}
                onChange={e => setForm(prev => ({ ...prev, primary_color: e.target.value }))}
                className="w-8 h-8 p-0 border-0 cursor-pointer"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#64748b] mb-2 block">Couleur secondaire</label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLOR_PRESETS.map(c => (
                <button
                  key={c}
                  onClick={() => setForm(prev => ({ ...prev, secondary_color: c }))}
                  className={`w-8 h-8 rounded-lg transition-all ${form.secondary_color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#0B0F19] scale-110" : "hover:scale-105"}`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <Input
                type="color"
                value={form.secondary_color}
                onChange={e => setForm(prev => ({ ...prev, secondary_color: e.target.value }))}
                className="w-8 h-8 p-0 border-0 cursor-pointer"
              />
            </div>
          </div>
        </div>
        {/* Preview */}
        <div className="mt-4 p-4 rounded-lg border border-[#1E293B]" style={{ borderColor: form.primary_color + "40" }}>
          <p className="text-xs text-[#64748b] mb-2">Apercu</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: form.primary_color + "20" }}>
              <Building2 className="w-5 h-5" style={{ color: form.primary_color }} />
            </div>
            <div>
              <p className="font-semibold" style={{ color: form.primary_color }}>{org?.org_name || "Mon Organisation"}</p>
              <p className="text-xs" style={{ color: form.secondary_color }}>{form.description?.slice(0, 60) || "Description de l'organisation"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#0099CC]" /> Reseaux sociaux
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: "social_facebook", icon: Facebook, label: "Facebook", placeholder: "https://facebook.com/..." },
            { key: "social_instagram", icon: Instagram, label: "Instagram", placeholder: "https://instagram.com/..." },
            { key: "social_linkedin", icon: Linkedin, label: "LinkedIn", placeholder: "https://linkedin.com/..." },
            { key: "social_twitter", icon: Twitter, label: "X / Twitter", placeholder: "https://x.com/..." },
          ].map(s => (
            <div key={s.key}>
              <label className="text-xs text-[#64748b] mb-1.5 flex items-center gap-1.5">
                <s.icon className="w-3 h-3" /> {s.label}
              </label>
              <Input
                value={form[s.key]}
                onChange={e => setForm(prev => ({ ...prev, [s.key]: e.target.value }))}
                placeholder={s.placeholder}
                className="bg-[#060B14] border-[#1E293B] text-white h-10"
                data-testid={`org-${s.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#10B981] hover:bg-[#0d9668] text-white px-8"
          data-testid="save-profile-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? "Sauvegarde..." : "Enregistrer le profil"}
        </Button>
      </div>
    </div>
  );
}
